// client/src/hooks/useLastVisitedRoute.ts (converted from .js, ST-00200)
//
// Route labels (tab titles, the back/forward bar's page name, the resume toast) and the
// "resume where you left off" memory. `getLabelForRoute` is the one place a path gets a
// human name when the sidebar has none. The memory is kept per signed-in user since PLT-20
// (ST-00230, utils/userStorage): on a shared PC nobody is offered someone else's page, and
// logout clears it.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { safeStorage, userStorageKey } from '../utils/userStorage';

const STORAGE_KEY = 'lb-last-visited-route';

const TOP_LEVEL_ROUTES = new Set(['/', '/login', '/unauthorized', '/settings', '/driver']);

const ROUTE_LABELS: Readonly<Record<string, string>> = {
  '/route-summary': 'Route Summary',
  '/product-movement': 'Product Movement',
  '/period-summary': 'Period Summary',
  '/location-analytics': 'Location Analytics',
  '/vehicle-tracker': 'Vehicle Tracker',
  // ST-00210: the live map is Live View; /geofencing is the Geofences page (zones, trips, …).
  '/geofencing': 'Geofences',
  '/lexistar': 'LexiStar',
  '/lexistar/live': 'Live View',
  '/lexistar/notifications': 'Notifications',
  '/lexistar/maintenance': 'Maintenance',
  '/custom-summaries': 'Custom Summaries',
  '/order-management': 'Order Management',
  '/order-management/orders': 'Orders',
  '/order-management/orders/create': 'Create Order',
  '/order-management/products': 'Products',
  '/order-management/locations': 'Locations',
  '/order-management/companies': 'Companies',
  '/order-management/vehicles': 'Vehicles',
  '/order-management/drivers': 'Drivers',
  '/order-management/bulk-upload': 'Bulk Upload',
  '/order-management/allocate': 'Haulier Allocation',
  '/amendments-allocate-transactions': 'Amendments',
  '/amendment-approvals': 'Amendment Approvals',
  '/amendment-analytics': 'Amendment Analytics',
  '/transaction-allocation': 'Transaction Allocation',
  '/pdf-generator-v2': 'PDF Generator',
  '/signatures': 'Signatures',
  '/email-reports': 'Email Reports',
  '/error-detection': 'Error Detection',
  '/route-intelligence': 'Route Intelligence',
  '/telegram-bi': 'Telegram BI',
  '/telegram-bi/queries': 'Telegram Queries',
  '/telegram-bi/templates': 'Telegram Templates',
  '/telegram-bi/schedules': 'Telegram Schedules',
  '/telegram-bi/analytics': 'Telegram Analytics',
  '/telegram-bi/bot': 'Telegram Bot Manager',
  '/edge-sync': 'Edge Sync',
  '/weighbridge-compliance': 'Compliance',
  // Settings → LexiStar (ST-00240, ST-00252): the card's pages by their own names, not
  // "Settings/Modules/Monitoring/…". "Notification setup" since ST-00251 ("Notifications" is the
  // inbox, /lexistar/notifications).
  '/settings/modules/monitoring/lexistar-notifications': 'Notification setup',
  '/settings/modules/monitoring/lexistar-assets': 'LexiStar Assets',
  '/settings/modules/monitoring/telemetry-devices': 'LexiStar Devices',
  '/settings/modules/monitoring/lexistar-routes': 'Routes & Lanes',
  '/settings/modules/monitoring/lexistar-fuel': 'Fuel Norms & Prices',
  '/settings/modules/monitoring/lexistar-webhooks': 'Integrations & webhooks',
  '/settings/modules/monitoring/lexistar-export': 'Data export',
  '/settings/modules/lexistar/maintenance-codes': 'Maintenance & delay codes',
  // Help & training (ST-00200): every guide is "Help & training" in the tab strip.
  '/help': 'Help & training',
  '/help/training': 'Training',
  '/help/glossary': 'Words we use',
};

/** What useLastVisitedPath stores (and ResumeToast reads back). */
export interface LastVisitedRoute {
  path: string;
  label: string;
  timestamp: number;
}

function isDeepRoute(pathname: string): boolean {
  if (TOP_LEVEL_ROUTES.has(pathname)) return false;
  if (pathname.startsWith('/settings/')) return false;
  return true;
}

export function getLastVisitedRoute(): LastVisitedRoute | null {
  // The page saved before it was kept per user belongs to whoever used the PC last.
  safeStorage.remove(STORAGE_KEY);
  const key = userStorageKey(STORAGE_KEY);
  if (!key) return null;
  try {
    const raw = safeStorage.get(key);
    if (!raw) return null;
    const data = JSON.parse(raw) as Partial<LastVisitedRoute> | null;
    if (!data || typeof data.path !== 'string' || !isDeepRoute(data.path)) return null;
    return data as LastVisitedRoute;
  } catch {
    return null;
  }
}

/** Forget the signed-in user's page (logout, or the toast used / dismissed). */
export function clearLastVisitedRoute(): void {
  safeStorage.remove(STORAGE_KEY);
  const key = userStorageKey(STORAGE_KEY);
  if (key) safeStorage.remove(key);
}

export function getLabelForRoute(pathname: string): string {
  const exact = ROUTE_LABELS[pathname];
  if (exact) return exact;
  // Longest mapped prefix at a path boundary (/help-desk is not under /help).
  const match = Object.keys(ROUTE_LABELS)
    .filter((r) => pathname.startsWith(`${r}/`))
    .sort((a, b) => b.length - a.length)[0];
  if (match) return ROUTE_LABELS[match] as string;
  // Unmapped route: derive a name from the path. Title-cased, because this is
  // shown as a heading and as a tab title, not as prose.
  return pathname
    .slice(1)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Records an explicit path. The tabbed shell renders outside every router — it
 * has no location to read — so it passes the active tab's path directly.
 */
export function useLastVisitedPath(path: string): void {
  const prevPath = useRef<string | null>(null);

  useEffect(() => {
    if (path === prevPath.current) return;
    prevPath.current = path;

    const key = userStorageKey(STORAGE_KEY);
    if (!key) return; // nobody signed in: nothing to remember for anyone
    if (isDeepRoute(path)) {
      safeStorage.set(
        key,
        JSON.stringify({
          path,
          label: getLabelForRoute(path),
          timestamp: Date.now(),
        } satisfies LastVisitedRoute)
      );
    } else {
      safeStorage.remove(key);
    }
  }, [path]);
}

/** Router-bound form, used by the untabbed shell. */
export default function useLastVisitedRoute(): void {
  const location = useLocation();
  useLastVisitedPath(location.pathname);
}
